import { useQueryClient } from '@tanstack/react-query';
import { excerptKeys } from '../queries';
import { codeKeys } from '../../codes/queries';
import { normalizeId } from '../../utils/idNormalization';
import {
  addExcerptToParagraphs,
} from '../../transcripts/queries';
import { addExcerptIdToTranscript } from '../../utils/transcriptCacheUtils';
import { mergeServerExcerptIntoDetail } from '../settleMerge';

/**
 * Invalidate code-related queries
 */
const invalidateCodeQueries = (queryClient, projectId) => {
  // Invalidate the codes list query to ensure codes appear properly
  if (projectId) {
    queryClient.invalidateQueries({
      queryKey: codeKeys.list(normalizeId(projectId))
    });
  }
};

/**
 * Hook to handle excerpt updates from ActionCable
 * @param {string|number} projectId - The project ID for invalidating codes list
 * @returns {Function} Function to handle excerpt updates from cable
 */
export function useUpdateExcerptFromCable(projectId) {
  const queryClient = useQueryClient();

  /**
   * Handle excerpt updates from cable
   * @param {Object} cableData - The data received from cable
   */
  const updateExcerptFromCable = (cableData) => {
    if (!cableData || !cableData.excerpt) {
      console.error('Invalid excerpt cable data received', cableData);
      return;
    }

    const { excerpt, id: transcriptId, client_id } = cableData;
    const excerptId = normalizeId(excerpt.id);
    const normalizedTranscriptId = normalizeId(transcriptId);

    // Skip if this is resolving an optimistic update from this session
    if (client_id) {
      const optimisticExcerpt = queryClient.getQueryData(excerptKeys.detail(client_id));
      if (optimisticExcerpt) {
        console.log('Skipping cable update for optimistic excerpt', client_id);
        return;
      }
    }

    // A broadcast is a server snapshot like any HTTP settle and settles
    // through the shared merge (see settleMerge.js for the full rules).
    // broadcast: true relaxes the gate to strictly-older — a TIE must
    // apply here, both for same-millisecond cross-client commits and for
    // memo-only broadcasts, whose excerpt updated_at is pre-touch.
    mergeServerExcerptIntoDetail(queryClient, excerpt, { broadcast: true });

    // Idempotent (checked-add per paragraph), so no existence pre-read:
    // for a plain coding update every id is already mapped and this
    // no-ops; for another coder's CREATE it maps the new excerpt.
    addExcerptToParagraphs(
      queryClient,
      normalizedTranscriptId,
      excerpt.codeable_type || 'Transcript',
      excerptId,
      excerpt.start,
      excerpt.end
    );

    if (excerpt?.codeable_type === 'Transcript') {
      // Membership add — the merge above always leaves a detail entry
      // (it creates one when none exists; the gate only skips writes over
      // an EXISTING entry), so the ordering invariant holds. No-ops for a
      // plain coding update.
      addExcerptIdToTranscript(queryClient, normalizedTranscriptId, excerptId);
    }

    // Invalidate code-related queries
    invalidateCodeQueries(queryClient, projectId);
  };

  return updateExcerptFromCable;
}
